import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CloseIcon } from "vega-icons";

import { getFullName } from "@vega-api/custom/utils/user";
import {
  getGetCategoriesQueryKey,
  getGetCategoryMembersQueryKey,
  useAddCategoryMembers,
  useClearCategoryDefaultAssignee,
  useGetCategoryMembers,
  useRemoveCategoryMember,
  useSetCategoryDefaultAssignee,
} from "@vega-api/generated/case-admin-v1/case-admin-v1";
import { useGetAssignableCaseManagers } from "@vega-api/generated/case-role-v1/case-role-v1";
import { type CategoryResponse } from "@vega-api/generated/types/categoryResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import {
  type BaseMultiSelectOption,
  ThemedMultiselectDropdown,
} from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { useMutationErrorToast } from "../../hooks/useMutationErrorToast";

type CategoryMembersSectionProps = {
  category: CategoryResponse;
};

export const CategoryMembersSection = ({
  category,
}: CategoryMembersSectionProps) => {
  const showToast = useToast();
  const errorToast = useMutationErrorToast();
  const queryClient = useQueryClient();
  const [pendingMembers, setPendingMembers] = useState<BaseMultiSelectOption[]>(
    [],
  );

  const { data: membersData, isLoading: isLoadingMembers } =
    useGetCategoryMembers(category.id);
  const { data: caseManagersData, isLoading: isLoadingCaseManagers } =
    useGetAssignableCaseManagers();

  const members = membersData?.data?.users ?? [];
  const caseManagers = caseManagersData?.data?.users ?? [];

  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: getGetCategoryMembersQueryKey(category.id),
    });
    queryClient.invalidateQueries({ queryKey: getGetCategoriesQueryKey() });
  };

  const onMembersChanged = () => {
    refresh();
    showToast({
      title: m.cases_settings_success_updateCategoryMembers(),
      variant: "success",
    });
  };

  const { mutate: addMembers, isPending: isAddingMembers } =
    useAddCategoryMembers({
      mutation: {
        onSuccess: () => {
          setPendingMembers([]);
          onMembersChanged();
        },
        onError: errorToast(m.cases_settings_error_updateCategoryMembers()),
      },
    });

  const { mutate: removeMember, isPending: isRemovingMember } =
    useRemoveCategoryMember({
      mutation: {
        onSuccess: onMembersChanged,
        onError: errorToast(m.cases_settings_error_updateCategoryMembers()),
      },
    });

  const { mutate: setDefaultAssignee, isPending: isSettingDefaultAssignee } =
    useSetCategoryDefaultAssignee({
      mutation: {
        onSuccess: onMembersChanged,
        onError: errorToast(m.cases_settings_error_updateDefaultAssignee()),
      },
    });

  const { mutate: clearDefaultAssignee, isPending: isClearingDefaultAssignee } =
    useClearCategoryDefaultAssignee({
      mutation: {
        onSuccess: onMembersChanged,
        onError: errorToast(m.cases_settings_error_updateDefaultAssignee()),
      },
    });

  const isPending =
    isAddingMembers ||
    isRemovingMember ||
    isSettingDefaultAssignee ||
    isClearingDefaultAssignee;

  const memberIds = new Set(members.map((member) => member.id));

  const addableOptions = caseManagers
    .filter((user) => isTruthy(user.id) && !memberIds.has(user.id))
    .map((user) => ({ label: getFullName(user), value: user.id as string }));

  const defaultAssigneeOptions = members
    .filter((member) => isTruthy(member.id))
    .map((member) => ({
      label: getFullName(member),
      value: member.id as string,
    }));

  return (
    <div className="space-y-3" data-testid="category-members-section">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <ThemedMultiselectDropdown
            isLoading={isLoadingCaseManagers}
            label={m.cases_settings_category_addMember()}
            name="categoryMembersToAdd"
            options={addableOptions}
            placeholder={m.cases_settings_category_addMemberPlaceholder()}
            searchEnabled
            selectedOptions={pendingMembers}
            setSelectedOptions={setPendingMembers}
          />
        </div>
        <ThemedButton
          dataTestId="category-add-members-button"
          disabled={pendingMembers.length === 0 || isPending}
          isLoading={isAddingMembers}
          onClick={() =>
            addMembers({
              categoryId: category.id,
              data: { userIds: pendingMembers.map((option) => option.value) },
            })
          }
          variant="secondary"
        >
          {m.cases_settings_category_addMembersAction()}
        </ThemedButton>
      </div>

      {members.length === 0 ? (
        <p
          className="body-small-regular text-text/secondary"
          data-testid="category-members-empty"
        >
          {m.cases_settings_category_membersEmpty()}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="category-members-list">
          {members.map((member) => (
            <li
              className="flex items-center justify-between gap-3"
              data-testid={`category-member-${member.id}`}
              key={member.id}
            >
              <span className="body-base-regular truncate">
                {getFullName(member)}
              </span>
              <ThemedIconButton
                ariaLabel={m.cases_settings_category_removeMemberAria({
                  name: getFullName(member),
                })}
                dataTestId={`category-member-remove-${member.id}`}
                disabled={isPending}
                icon={<CloseIcon className="h-4 w-4" />}
                onClick={() =>
                  removeMember({
                    categoryId: category.id,
                    userId: member.id as string,
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}

      <ThemedDropdown
        isClearable
        isLoading={isLoadingMembers}
        label={m.cases_settings_category_defaultAssignee()}
        name="categoryDefaultAssignee"
        onChange={(option) => {
          if (option?.value) {
            setDefaultAssignee({
              categoryId: category.id,
              data: { userId: option.value },
            });
          } else {
            clearDefaultAssignee({ categoryId: category.id });
          }
        }}
        options={defaultAssigneeOptions}
        placeholder={m.cases_settings_category_defaultAssigneePlaceholder()}
        value={category.defaultAssigneeId ?? ""}
      />
    </div>
  );
};
